import { useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridRowParams, GridValidRowModel } from "@mui/x-data-grid";
import { formatNumber } from "@/utils/numberUtils";
import { mergeDataTableStyles } from "./styles";
import { EmptyOverlay } from "./slots/EmptyOverlay";
import { ErrorOverlay } from "./slots/ErrorOverlay";
import { LoadingOverlay } from "./slots/LoadingOverlay";
import type { DataTableProps } from "./types";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * DataTable - A reusable wrapper around MUI DataGrid.
 *
 * Features:
 * - Pre-configured styles (scrollbars, headers, cells)
 * - Built-in error and empty states
 * - Server-side pagination and sorting support
 * - Generic TypeScript support for row types
 *
 * @example
 * // Basic usage with server-side pagination
 * <DataTable
 *   rows={data}
 *   columns={columns}
 *   getRowId={(row) => row.id}
 *   rowCount={totalCount}
 *   paginationModel={paginationModel}
 *   onPaginationModelChange={setPaginationModel}
 *   loading={isLoading}
 *   error={error}
 * />
 *
 * @example
 * // With useDataTableState hook
 * const tableState = useDataTableState({ initialPageSize: 25 });
 * <DataTable
 *   rows={data}
 *   columns={columns}
 *   getRowId={(row) => row.id}
 *   {...tableState}
 * />
 */
export function DataTable<T extends GridValidRowModel>({
  // Required
  rows,
  columns,
  getRowId,

  // Pagination
  rowCount,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,

  // Sorting
  sortModel,
  onSortModelChange,

  // States
  loading = false,
  error = null,
  emptyMessage = "No data available",

  // Interactions
  onRowClick,

  // Customization
  slots,
  sx,

  // Escape hatch
  dataGridProps,
}: DataTableProps<T>) {
  // Determine if we're in server-side mode
  const isServerSide = Boolean(onPaginationModelChange || onSortModelChange);

  // Merged styles
  const mergedSx = useMemo(() => mergeDataTableStyles(sx), [sx]);

  // Row click handler
  const handleRowClick = useCallback(
    (params: GridRowParams<T>) => {
      onRowClick?.(params.row);
    },
    [onRowClick]
  );

  // Get custom overlay components
  const CustomEmptyOverlay = slots?.empty ?? EmptyOverlay;
  const CustomErrorOverlay = slots?.error ?? ErrorOverlay;

  // Render error state
  if (error) {
    return (
      <Box sx={{ height: "100%", minHeight: 300 }}>
        <CustomErrorOverlay error={error} />
      </Box>
    );
  }

  // Render empty state (only when not loading and no rows)
  const showEmptyState = !loading && rows.length === 0;
  if (showEmptyState) {
    return (
      <Box sx={{ height: "100%", minHeight: 300 }}>
        <CustomEmptyOverlay message={emptyMessage} />
      </Box>
    );
  }

  return (
    <DataGrid<T>
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      // Pagination
      pageSizeOptions={pageSizeOptions}
      paginationMode={isServerSide ? "server" : "client"}
      rowCount={rowCount ?? rows.length}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      // Sorting
      sortingMode={isServerSide ? "server" : "client"}
      sortModel={sortModel}
      sortingOrder={["asc", "desc"]}
      onSortModelChange={onSortModelChange}
      // Interactions
      onRowClick={onRowClick ? handleRowClick : undefined}
      loading={loading}
      // Disable features we don't use
      disableColumnFilter
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnResize
      disableColumnSelector
      // Pagination display
      localeText={{
        paginationDisplayedRows: ({ from, to, count }) => {
          return `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(count)}`;
        },
      }}
      slotProps={{
        ...dataGridProps?.slotProps,
        pagination: {
          ...dataGridProps?.slotProps?.pagination,
          showFirstButton: true,
          showLastButton: true,
        },
      }}
      // Slots
      slots={{
        loadingOverlay: LoadingOverlay,
        toolbar: slots?.toolbar,
        row: slots?.row,
        ...dataGridProps?.slots,
      }}
      // Styles
      sx={mergedSx}
      // Forward any additional props
      {...dataGridProps}
    />
  );
}
